import Button from '@rio-cloud/rio-uikit/Button';
import Table, { TableBody, TableColumn, TableHeader, TableHeaderColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import { FormattedMessage } from 'react-intl';
import { type Role, Access, type RoleDefinition, Service } from './RoleAccessMatrix.ts';

const getAccessIcon = (access: Access) => {
    let icon = 'uikit-icon icon-';
    switch (access) {
        case Access.FULL:
            icon += 'ok text-color-success';
            break;
        case Access.RESTRICTED:
            icon += 'ok-dashed text-color-warning text-size-18';
            break;
        case Access.NO:
            icon += 'remove text-color-danger';
            break;
        default:
            break;
    }
    return icon;
};

interface RoleDescriptionTableProps {
    fullContent: Record<Role, RoleDefinition>;
    switchRoleFn: (role: Role) => void;
}

export const RoleDescriptionTable = ({ fullContent, switchRoleFn }: RoleDescriptionTableProps) => {
    const serviceKeys = Object.keys(fullContent.Administrator.services) as Array<keyof typeof Service>;
    const rows = Object.entries(fullContent) as [Role, RoleDefinition][];

    return (
        <>
            <h4 className='margin-bottom-10'>
                <FormattedMessage id={'intl-msg:roles.available'} />
            </h4>
            <p className='text-wrap-balance'>
                <FormattedMessage id={'intl-msg:header.info.entities.usergrouproles.linkText'} />{' '}
                <FormattedMessage id={'intl-msg:header.info.entities.usergrouproles.text'} />
            </p>
            <div>
                <div className={'margin-bottom-20'}>
                    <Table rowKey='id' stickyColumns={{ left: 1 }}>
                        <TableHeader>
                            <TableHeaderColumn columnKey='role' width={260}>
                                <FormattedMessage id={'intl-msg:roles.description.tableDeclaration'} />
                            </TableHeaderColumn>
                            {serviceKeys.map(serviceKey => {
                                const serviceName = Service[serviceKey];

                                return (
                                    <TableHeaderColumn columnKey={serviceKey} key={serviceKey}>
                                        <div className='text-center'>
                                            <FormattedMessage id={`${serviceName}.short`} />
                                        </div>
                                    </TableHeaderColumn>
                                );
                            })}
                        </TableHeader>
                        <TableBody>
                            {rows.map(([role, definition]) => (
                                <TableRow id={role} key={role}>
                                    <TableColumn columnKey='role'>
                                        <div className='text-left text-light'>
                                            <Button
                                                onClick={() => switchRoleFn(role)}
                                                variant={Button.VARIANT_LINK_INLINE}
                                                autoTrackingKey={`${definition.title.replace('intl-msg:', '')}.open`}
                                            >
                                                <FormattedMessage id={definition.title} />
                                            </Button>
                                        </div>
                                    </TableColumn>
                                    {serviceKeys.map(serviceKey => {
                                        const access = definition.services[serviceKey];

                                        return (
                                            <TableColumn columnKey={serviceKey} key={`${role}:${serviceKey}`}>
                                                <div className='text-center'>
                                                    {access ? <span className={getAccessIcon(access.access)} /> : null}
                                                </div>
                                            </TableColumn>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div>
                    <span className={'margin-right-20'}>
                        <span className={'margin-right-5 uikit-icon icon-ok text-color-success'} />
                        <FormattedMessage id={Access.FULL} />
                    </span>
                    <span className={'margin-right-20'}>
                        <span className={'margin-right-5 uikit-icon icon-ok text-color-warning'} />
                        <FormattedMessage id={Access.RESTRICTED} />
                    </span>
                    <span>
                        <span className={'margin-right-5 uikit-icon icon-remove text-color-danger'} />
                        <FormattedMessage id={Access.NO} />
                    </span>
                </div>
            </div>
        </>
    );
};
